// Unified Settings modal (workspace + project canvas + tickets), extracted from
// pages/studios.js as Stage 1 of the studios barrel-split (#11). It must stay
// EAGER: the /tickets Sidebar + TicketsKanban and workspace/app.js all open it
// via `extends: 'SettingsModalBody'`, and those surfaces never load the studios
// page chunk. Kept self-contained — the only studios-page dependencies were the
// live board-engine calls (drag mode + auto-layout), which are decoupled here
// via `el.call` so they run only when the studios page has registered them (on
// /studio) and gracefully no-op everywhere else.
//
// This module does NOT import pages/studios.js — that edge would re-pin the
// board/panzoom/connection engine into the initial bundle and defeat Stage 2.

// Factory for SettingsModal tabs — left-rail button that picks a section.
// Reuses the house MenuPanelItem row (button chrome, padding, title color,
// theme-aware hover); active state is the same `selected` wash every other
// active item in the suite uses (DESIGN_LANGUAGE.md §4.3 — overlayStrong is
// retired from state duty).
//
// The tab key rides on the tab's own `scope`, and `isActive` and `onClick`
// read `el.scope.tabKey`: frank serializes each handler from its own source
// text, so a handler that read the `tabKey` parameter carried a free name into
// the emitted record (no tab showed active, a click could not leave Workspace).
// The `.isActive` conditional block marks the open tab and follows
// `root.settingsTab` on its own, inside this custom modal too: the key is
// absent until the Settings button writes it, and such a block stays live
// (smbls bf7d72268). A click only writes the tab; no handler moves a class
// (__tests__/settingsModalNoHandToggle.test.js).
function settingsTab(tabKey, label, hint) {
  return {
    extends: 'MenuPanelItem',
    scope: { tabKey },
    // `text` takes the template form; `title` is an HTML attribute, so its
    // caller passes the FUNCTION form (docs/I18N-CONVENTION.md §1).
    text: label,
    title: hint,
    isActive: (el, s) => (s.root?.settingsTab || 'workspace') === el.scope?.tabKey,
    '.isActive': {
      background: 'selected',
      color: 'title',
      fontWeight: '700'
    },
    onClick: (e, el, s) => {
      e.stopPropagation()
      s.root?.update({ settingsTab: el.scope?.tabKey })
    }
  }
}

// `_settingsSwitchIsOn`/`_settingsSwitchToggle` are the generic read/write
// dispatch for `iosSwitch` below (2026-08-13, tickets/opus.md "~800
// project-side Frankability defects" — SettingsModal's
// factory-closure-capture class, 8 unresolvable-scope-read findings via
// `smbls frank to-json .`). `iosSwitch` used to take `{isOn, onToggle,
// disabledHint}` as params and close over them directly inside `onClick`/
// `onRender` — the SAME frank-fatal shape as CanvasOverlayV2's
// `handle`/`ruler`, except the captured value was a FUNCTION, not a
// primitive (the harder case that ticket flags): `onToggle` itself closed
// over `settingsCanvasRow`'s own `opts` (`opts.patchSnap`/`opts.call`/
// `opts.patch`/`opts.checked`), so putting the closure itself on `scope:`
// would only move the same free-variable problem one level down, onto
// `opts`. Traced every one of the 8 call sites instead of assuming this was
// unfixable: all 8 reduce to exactly 3 primitive dispatch shapes — read
// `root|snap` × `strict|truthy`, write `call|patch|patchSnap` — so the
// closures were genuinely eliminable, not an exception. These two functions
// are real top-level declarations (never received through a factory's own
// params) that read ONLY their own `(el, s)` params + `el.scope` — the same
// shape as PageSlot's `_histCan(el, s)` fix.
function _settingsSwitchIsOn(el) {
  try {
    const v2 = el.call?.('getCanvasV2')
    const key = el.scope?.key
    if (!v2 || !key) return false
    const value = el.scope?.group === 'snap' ? v2.snap?.[key] : v2[key]
    return el.scope?.mode === 'strict' ? value === true : !!value
  } catch (err) {
    return false
  }
}
function _settingsSwitchToggle(v, el) {
  try {
    if (!el.call?.('getCanvasV2')) return false
    const key = el.scope?.key
    if (el.scope?.action === 'call') el.call?.(el.scope.callFn)
    else if (el.scope?.action === 'patchSnap') el.call?.('patchSnap', { [key]: v })
    else if (el.scope?.action === 'patch') el.call?.('patchCanvas', { [key]: v })
  } catch (err) {
    console.warn('[settings] canvas patch failed:', err)
    return false
  }
}

// iOS-style switch — pill track + white knob. The conditional `.isOn` block
// drives the on-state (track fill + knob side) and follows the setting on its
// own: `isOn` reads root `canvas` through getCanvasV2, so the root write a
// toggle makes re-runs it. A click takes the next value from the setting
// itself, never from the node. `scope` carries the per-row dispatch
// primitives (`group`/`key`/`mode`/`action`/`callFn`) that
// `_settingsSwitchIsOn`/`_settingsSwitchToggle` above read via `el.scope`.
function iosSwitch(scope) {
  return {
    tag: 'button',
    type: 'button',
    width: '44px',
    minWidth: '44px',
    height: '27px',
    round: 'radiusPill',
    borderWidth: '0',
    cursor: 'pointer',
    padding: '2px',
    display: 'flex',
    align: 'center left',
    transition: 'background 160ms ease',
    // Fixed-colour sweep: kept — track (gray8.36) + on-state fill (#34c759)
    // + Knob (white below) together form the universal iOS-style switch,
    // deliberately theme-independent regardless of app theme or track
    // colour, same convention already established for workspace's
    // SwitchInput.Knob.
    background: 'gray8.36',
    scope,
    isOn: (el, s) => _settingsSwitchIsOn(el, s),
    '.isOn': { background: '#34c759', align: 'center right' },
    title: (el) => el.call('polyglot', 'canvas.settings.switchTitle'),
    Knob: {
      tag: 'span',
      width: '23px',
      height: '23px',
      round: '50%',
      background: 'white',
      pointerEvents: 'none'
    },
    onClick: (e, el, s) => {
      e.stopPropagation()
      _settingsSwitchToggle(!_settingsSwitchIsOn(el, s), el, s)
    }
  }
}

// Grouped-list row chrome (iOS inset list): label + hint left, control right.
// Was a `borderBottomWidth: 'hairline'` separator between rows (plus a raw
// CSS-var borderBottomColor instead of a token) — row-separator-as-stroke,
// banned by design-direction.md §8 / DESIGN_LANGUAGE §2.5 (same fix as
// workspace-shell's SettingRow.js/FlagRow.js). Each row's own top+bottom
// `Z1` padding already spaces adjacent rows regardless of the pane's own
// `gap: '0'` (flex padding is never collapsed away by a parent gap), so
// dropping the line changes nothing geometrically; the hover wash is the
// fill-contrast replacement for the boundary cue.
const _settingsRowChrome = {
  flow: 'x',
  align: 'center space-between',
  gap: 'A',
  padding: 'Z1 0',
  transition: 'Z defaultBezier background',
  ':hover': { background: 'hover' },
  '@mobileL': {
    flow: 'y',
    align: 'stretch flex-start',
    gap: 'Y'
  }
}

const _settingsPaneChrome = {
  flow: 'y',
  gap: '0',
  round: 'radiusCard',
  padding: '0 A A',
  // Fixed-colour sweep: white.04 -> title.04, same alpha. SettingsModalBody
  // is opened via el.call('openModal', ...) and rendered inside the brand
  // ModalWindow, which carries an adaptive `theme: 'modal'`
  // (background:'gray2' dark / 'gray16' light) — a fixed white wash on this
  // grouped-list pane was already near-invisible on the light-mode gray16
  // surface, same "subtle wash card on an adaptive modal" bug already fixed
  // in tickets/ (ActivityHeatmap, DailyUpdate, TicketDetailModal).
  background: 'title.04'
}

const _ticketsSegmentedControlChrome = {
  flow: 'x',
  gap: '0',
  round: 'radiusPill',
  padding: 'W',
  background: 'line-faded',
  childExtends: 'Button',
  childProps: {
    minHeight: 'controlHCompact',
    padding: '0 Y',
    fontSize: 'Z',
    round: 'radiusPill',
    cursor: 'pointer',
    borderWidth: '0',
    background: 'transparent',
    color: 'caption',
    '.isActive': {
      background: 'selected',
      color: 'title',
      fontWeight: '700'
    }
  }
}

// Factory for canvas-scoped settings rows. Always rendered — without an open
// project the switch reads off and explains itself via title; the patch calls
// are guarded no-ops. `opts` is now plain data (`group`/`key`/`mode`/
// `action`/`callFn`) — no functions — so it rides on `iosSwitch`'s `scope:`
// intact (see `_settingsSwitchIsOn`/`_settingsSwitchToggle` above for why).
function settingsCanvasRow(label, hint, opts = {}) {
  return {
    ..._settingsRowChrome,
    Label: {
      flow: 'y',
      gap: 'V',
      flexGrow: '1',
      Name: { tag: 'span', text: label, fontWeight: '700', color: 'title' },
      Hint: { tag: 'span', text: hint, fontSize: 'Z', color: 'caption' }
    },
    Control: iosSwitch({
      group: opts.group === 'snap' ? 'snap' : 'root',
      key: opts.key,
      mode: opts.mode === 'strict' ? 'strict' : 'truthy',
      action: opts.action,
      callFn: opts.callFn || null
    })
  }
}

// Factory for SettingsModal content rows — label + hint on the left, a control
// (toggle, button, input) on the right. Takes the control def directly so
// callers own its state/handlers.
function settingsBlockRow(label, hint, control) {
  return {
    ..._settingsRowChrome,
    Label: {
      flow: 'y',
      gap: 'V',
      flexGrow: '1',
      minWidth: '0',
      Name: { tag: 'span', text: label, fontWeight: '700', color: 'title' },
      Hint: { tag: 'span', text: hint, fontSize: 'Z', color: 'caption' }
    },
    Control: {
      ...control,
      flexShrink: '0'
    }
  }
}

// ── Tickets-section helpers ──────────────────────────────────────────────
// The /tickets board keeps its per-column UI prefs on root: `_colCount`
// (cards per row, default 2) and `_hiddenCols` (columns hidden via the column
// "⋯" menu). Column keys come from root.columns with the same fallback
// derivation the kanban itself uses.
const ticketsColKeys = (root) => {
  const cols = (root?.columns || []).map((c) => c.key)
  if (cols.length) return cols
  const counted = Object.keys(root?.totalByColumn || {})
  if (counted.length) return counted
  return [...new Set((root?.tickets || []).map((t) => t.columnKey).filter(Boolean))]
}
// A column's name as the board header shows it: the tickets package's
// registered columnLabelText, reached through el.call like this tab's other
// tickets functions (setTicketsBoardPref, refreshTicketsCounts). A column the
// server marks `labelIsSeed` reads in the active language; any other column
// shows its stored label, and so does every column while columnLabelText is
// not registered. A key with no column row title-cases, as before.
const ticketsColLabel = (el, root, key) => {
  const col = (root?.columns || []).find((c) => c.key === key)
  return (
    (col && (el.call('columnLabelText', col) || col.label)) ||
    String(key)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
  )
}
const ticketsHiddenKeys = (root) => {
  const hidden = root?._hiddenCols || {}
  return Object.keys(hidden).filter((k) => hidden[k])
}
const ticketsCardsPerRow = (root) => {
  const m = root?._colCount || {}
  const keys = ticketsColKeys(root)
  return keys.length && keys.every((k) => m[k] === 1) ? 1 : 2
}
// Persist through the tickets package's registered setTicketsBoardPref
// (root.update + localStorage) so the choice survives reloads (2026-07-25
// per Nika "make sure settings changes persist after reload").
const setTicketsCardsPerRow = (el, root, n) => {
  const m = {}
  ticketsColKeys(root).forEach((k) => {
    m[k] = n
  })
  el.call('setTicketsBoardPref', { _colCount: m })
}

// Unified settings modal for workspace, project (canvas) and tickets pages.
// `root.settingsTab` picks the active section. Body-only content for the
// @symbo.ls/windows custom layer (id 'settings'); the ModalLayer provides the
// backdrop + ESC/backdrop dismiss. Opened via el.call('openModal', { id:
// 'settings', content: { extends: 'SettingsModalBody' } }).
//
// Keys prefixed `Settings*` so DOMQL's key-based auto-extend doesn't merge us
// with global Card / Header / Body / Content / ProjectSection.
export const SettingsModalBody = {
  position: 'relative',
  flow: 'y',
  gap: 'B',
  minWidth: '0',

  // Brand window header — its Close icon (data-key="Close") is dismissed by
  // the ModalRoot's document delegate.
  ModalHeader: {
    position: 'relative',
    Hgroup: {
      H: { text: '{{ canvas.settings.title | polyglot }}' },
      P: { text: '{{ canvas.settings.subtitle | polyglot }}' }
    },
    Icons: {
      Close: {
        position: 'absolute',
        top: '0',
        right: '0',
        margin: '0'
      }
    }
  },

  SettingsBody: {
    flow: 'x',
    gap: 'B',
    flexGrow: '1',
    maxHeight: '72vh',
    overflow: 'hidden',

    WsSettingsNav: {
      flow: 'y',
      width: 'E',
      minWidth: 'E',
      gap: 'Y',

      NavItemWorkspace: settingsTab(
        'workspace',
        '{{ canvas.settings.tabWorkspace.label | polyglot }}',
        (el) => el.call('polyglot', 'canvas.settings.tabWorkspace.hint')
      ),
      NavItemProject: settingsTab(
        'project',
        '{{ canvas.settings.tabProject.label | polyglot }}',
        (el) => el.call('polyglot', 'canvas.settings.tabProject.hint')
      ),
      NavItemTickets: settingsTab(
        'tickets',
        '{{ canvas.settings.tabTasks.label | polyglot }}',
        (el) => el.call('polyglot', 'canvas.settings.tabTasks.hint')
      )
    },

    SettingsContent: {
      flexGrow: '1',
      flexBasis: '0',
      flow: 'y',
      minWidth: '0',
      overflowX: 'hidden',
      overflowY: 'auto',
      gap: 'A',

      WorkspacePane: {
        ..._settingsPaneChrome,
        display: (el, s) =>
          (s.root?.settingsTab || 'workspace') === 'workspace' ? 'flex' : 'none',
        // iOS grouped inset list — the pane is the rounded group card, rows
        // separate by their own padding + a hover wash (see
        // _settingsRowChrome).

        DragModeRow: settingsBlockRow(
          '{{ canvas.settings.dragMode.label | polyglot }}',
          '{{ canvas.settings.dragMode.hint | polyglot }}',
          {
            flow: 'x',
            gap: '0',
            round: 'radiusPill',
            padding: 'W',
            background: 'line-faded',
            childExtends: 'Button',
            childProps: {
              minHeight: 'controlHCompact',
              padding: '0 Y',
              fontSize: 'Z',
              round: 'radiusPill',
              cursor: 'pointer',
              borderWidth: '0',
              background: 'transparent',
              color: 'caption',
              '.isActive': { background: 'selected', color: 'title' }
            },
            // Drag mode reads/writes the studios board's live state. Off-studio
            // (studios page unmounted) el.call('getDragMode') returns undefined
            // → default to 'grid' so the segmented control still shows a
            // sensible active state instead of both segments dark.
            FreeformBtn: {
              text: '{{ canvas.settings.dragMode.free | polyglot }}',
              isActive: (el, s) => (el.call('getDragMode', s.root) || 'grid') === 'freeform',
              onClick: (e, el, s) => {
                s.root.update({ dragMode: 'freeform' })
              }
            },
            GridBtn: {
              text: '{{ canvas.settings.dragMode.grid | polyglot }}',
              isActive: (el, s) => (el.call('getDragMode', s.root) || 'grid') === 'grid',
              onClick: (e, el, s) => {
                s.root.update({ dragMode: 'grid' })
              }
            }
          }
        ),

        AutoLayoutRow: settingsBlockRow(
          '{{ canvas.settings.autoLayout.label | polyglot }}',
          '{{ canvas.settings.autoLayout.hint | polyglot }}',
          {
            tag: 'button',
            type: 'button',
            minHeight: 'controlHCompact',
            padding: '0 Z',
            round: 'radiusPill',
            borderWidth: '0',
            background: 'line-faded',
            color: 'title',
            fontSize: 'Z',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            text: '{{ canvas.settings.autoLayout.run | polyglot }}',
            ':hover': { background: 'line-highlight' },
            '@mobileL': { alignSelf: 'flex-start' },
            onClick: (e, el, s) => {
              e.stopPropagation()
              const root = s.root
              if (!Array.isArray(root?.nodes)) return
              // Auto-layout drives the studio board's live panzoom/layout +
              // canvas-layout persistence engine, which exists only while the
              // studios page is mounted (it registers these fns on /studio).
              // Off-studio el.call returns undefined → guard to a no-op so this
              // never throws on /tickets or the project canvas (and never asks
              // for a rearrange that cannot run).
              if (!el.call('layoutPositions', root.nodes)) return
              // It replaces every hand-placed position in the workspace-shared
              // layout (every member sees it) and the board has no undo: the
              // house confirm first (CRUD + confirm standard rule 4). Nothing
              // moves before the verb.
              el.call('confirmDialog', {
                title: el.call('polyglot', 'canvas.settings.autoLayout.confirmTitle'),
                message: el.call('polyglot', 'canvas.settings.autoLayout.confirmMessage'),
                confirmText: el.call('polyglot', 'canvas.settings.autoLayout.confirmButton'),
                cancelText: el.call('polyglot', 'common.action.cancel'),
                confirmTheme: 'primary',
                icon: null,
                onConfirm: () => {
                  const newPositions = el.call('layoutPositions', root.nodes)
                  if (!newPositions) return
                  root.update({ positions: newPositions })
                  el.call(
                    'persistPositions',
                    {
                      nodes: root.nodes,
                      positions: newPositions,
                      orgId: root.orgId
                    },
                    root.nodes.map((n) => n.id)
                  )
                }
              })
            }
          }
        )
      },

      ProjectPane: {
        ..._settingsPaneChrome,
        display: (el, s) => ((s.root?.settingsTab || 'workspace') === 'project' ? 'flex' : 'none'),

        // Canvas v2 settings — ALWAYS rendered; without an open project the
        // switches read off and patches are guarded no-ops; the footnote below
        // explains the scope.
        ShowDesignSystemRow: settingsCanvasRow(
          '{{ canvas.settings.showDesignSystem.label | polyglot }}',
          '{{ canvas.settings.showDesignSystem.hint | polyglot }}',
          {
            key: 'showDesignSystem',
            mode: 'strict',
            action: 'call',
            callFn: 'toggleCanvasDesignSystem'
          }
        ),
        ShowComponentsRow: settingsCanvasRow(
          '{{ canvas.settings.showComponents.label | polyglot }}',
          '{{ canvas.settings.showComponents.hint | polyglot }}',
          {
            key: 'showComponents',
            mode: 'strict',
            action: 'call',
            callFn: 'toggleCanvasComponents'
          }
        ),
        ShowRulersRow: settingsCanvasRow(
          '{{ canvas.settings.showRulers.label | polyglot }}',
          '{{ canvas.settings.showRulers.hint | polyglot }}',
          {
            key: 'showRulers',
            mode: 'truthy',
            action: 'patch'
          }
        ),
        ShowMinimapRow: settingsCanvasRow(
          '{{ canvas.settings.showMinimap.label | polyglot }}',
          '{{ canvas.settings.showMinimap.hint | polyglot }}',
          {
            key: 'showMinimap',
            mode: 'truthy',
            action: 'patch'
          }
        ),
        SnapRow: settingsCanvasRow(
          '{{ canvas.settings.snap.label | polyglot }}',
          '{{ canvas.settings.snap.hint | polyglot }}',
          {
            group: 'snap',
            key: 'enabled',
            mode: 'truthy',
            action: 'patchSnap'
          }
        ),
        SnapGridRow: settingsCanvasRow(
          '{{ canvas.settings.snapGrid.label | polyglot }}',
          '{{ canvas.settings.snapGrid.hint | polyglot }}',
          {
            group: 'snap',
            key: 'toGrid',
            mode: 'truthy',
            action: 'patchSnap'
          }
        ),
        SnapItemsRow: settingsCanvasRow(
          '{{ canvas.settings.snapItems.label | polyglot }}',
          '{{ canvas.settings.snapItems.hint | polyglot }}',
          {
            group: 'snap',
            key: 'toItems',
            mode: 'truthy',
            action: 'patchSnap'
          }
        ),
        SnapGuidesRow: settingsCanvasRow(
          '{{ canvas.settings.snapGuides.label | polyglot }}',
          '{{ canvas.settings.snapGuides.hint | polyglot }}',
          {
            group: 'snap',
            key: 'toGuides',
            mode: 'truthy',
            action: 'patchSnap'
          }
        ),

        // iOS-style group footnote — scope hint, shown only when no project
        // canvas is open (the switches above still render).
        ProjectScopeNote: {
          hide: (el) => {
            try {
              return !!el.call?.('getCanvasV2')
            } catch (err) {
              return false
            }
          },
          tag: 'span',
          padding: 'Z 0',
          fontSize: 'Z',
          color: 'caption',
          text: '{{ canvas.settings.projectScopeNote | polyglot }}'
        }
      },

      TicketsPane: {
        ..._settingsPaneChrome,
        display: (el, s) => ((s.root?.settingsTab || 'workspace') === 'tickets' ? 'flex' : 'none'),

        CardsPerRowRow: settingsBlockRow(
          '{{ canvas.settings.cardsPerRow.label | polyglot }}',
          '{{ canvas.settings.cardsPerRow.hint | polyglot }}',
          {
            ..._ticketsSegmentedControlChrome,
            OneColBtn: {
              text: '1',
              isActive: (el, s) => ticketsCardsPerRow(s.root) === 1,
              onClick: (e, el, s) => setTicketsCardsPerRow(el, s.root, 1)
            },
            TwoColBtn: {
              text: '2',
              isActive: (el, s) => ticketsCardsPerRow(s.root) === 2,
              onClick: (e, el, s) => setTicketsCardsPerRow(el, s.root, 2)
            }
          }
        ),

        // Freeform card placement — when on, cards can be dragged out of their
        // column and placed anywhere on the board canvas; dropping a freeform
        // card back onto a column returns it to in-column flow.
        CardPlacementRow: settingsBlockRow(
          '{{ canvas.settings.cardPlacement.label | polyglot }}',
          '{{ canvas.settings.cardPlacement.hint | polyglot }}',
          {
            ..._ticketsSegmentedControlChrome,
            // Freeform is the DEFAULT mode (2026-07-25 per Nika) — an unset
            // flag reads as ON; both buttons persist via setTicketsBoardPref
            // so the mode survives reloads.
            InColumnsBtn: {
              text: '{{ canvas.settings.cardPlacement.inColumns | polyglot }}',
              isActive: (el, s) => s.root?._ticketsFreeform === false,
              onClick: (e, el, s) => el.call('setTicketsBoardPref', { _ticketsFreeform: false })
            },
            FreeformBtn: {
              text: '{{ canvas.settings.cardPlacement.freeform | polyglot }}',
              isActive: (el, s) => s.root?._ticketsFreeform !== false,
              onClick: (e, el, s) => el.call('setTicketsBoardPref', { _ticketsFreeform: true })
            }
          }
        ),

        HiddenColsBlock: {
          padding: 'Z1 0',
          flow: 'y',
          gap: 'Y',
          HiddenColsLabel: {
            flow: 'y',
            gap: 'V',
            Name: {
              tag: 'span',
              text: '{{ canvas.settings.hiddenCols.label | polyglot }}',
              fontWeight: '700',
              color: 'title'
            },
            Hint: {
              tag: 'span',
              text: '{{ canvas.settings.hiddenCols.hint | polyglot }}',
              fontSize: 'Z',
              color: 'caption'
            }
          },
          HiddenColsEmpty: {
            hide: (el, s) => ticketsHiddenKeys(s.root).length > 0,
            tag: 'span',
            fontSize: 'Z',
            color: 'caption',
            text: '{{ canvas.settings.hiddenCols.empty | polyglot }}'
          },
          HiddenColsList: {
            hide: (el, s) => !ticketsHiddenKeys(s.root).length,
            flow: 'y',
            gap: 'Y',
            childrenAs: 'state',
            children: (el, s) =>
              ticketsHiddenKeys(s.root).map((key) => ({
                key,
                label: ticketsColLabel(el, s.root, key)
              })),
            childExtends: {
              flow: 'x',
              align: 'center space-between',
              gap: 'A',
              padding: 'Y Z1',
              round: 'radiusControl',
              background: 'line-faded',
              ColName: {
                tag: 'span',
                fontSize: 'Z1',
                color: 'title',
                text: (el, s) => s.label
              },
              ShowColBtn: {
                tag: 'button',
                type: 'button',
                text: '{{ canvas.settings.hiddenCols.show | polyglot }}',
                minHeight: 'controlHCompact',
                padding: '0 Z',
                round: 'radiusPill',
                borderWidth: '0',
                background: 'transparent',
                color: 'title',
                cursor: 'pointer',
                fontSize: 'Z',
                ':hover': { background: 'line-highlight' },
                onClick: (e, el, s) => {
                  const hidden = { ...(s.root?._hiddenCols || {}) }
                  delete hidden[s.key]
                  el.call('setTicketsBoardPref', { _hiddenCols: hidden })

                  const raw = s.root?._rawTypeCounts
                  if (raw) {
                    const nextTypes = {}
                    for (const bucket in raw) {
                      nextTypes[bucket] = Object.entries(raw[bucket]).reduce((sum, [cKey, v]) => {
                        if (s.root._excludedCols?.[cKey] || hidden[cKey]) return sum
                        return sum + (Number(v) || 0)
                      }, 0)
                    }
                    s.root.update({ totalByType: nextTypes })
                  }
                  // The board re-reads its counts (the registered tickets
                  // function; it reads nothing while no board is on screen).
                  el.call('refreshTicketsCounts')
                }
              }
            }
          }
        },

        ExcludedColsBlock: {
          padding: 'Z1 0',
          flow: 'y',
          gap: 'Y',
          state: { hasChanges: false },
          ExcludedColsHeader: {
            flow: 'x',
            align: 'start space-between',
            gap: 'A',
            ExcludedColsLabel: {
              flow: 'y',
              gap: 'V',
              Name: {
                tag: 'span',
                text: '{{ canvas.settings.excludedCols.label | polyglot }}',
                fontWeight: '700',
                color: 'title'
              },
              Hint: {
                tag: 'span',
                text: '{{ canvas.settings.excludedCols.hint | polyglot }}',
                fontSize: 'Z',
                color: 'caption'
              }
            },
            SaveBtn: {
              extends: 'Button',
              theme: 'primary',
              text: '{{ common.action.save | polyglot }}',
              flexShrink: '0',
              ':disabled': {
                theme: 'field-disabled',
                cursor: 'not-allowed'
              },
              disabled: (el, s) => !s.hasChanges,
              onClick: (e, el, s) => {
                if (!s.hasChanges) return
                const root = s.root
                const raw = root._rawTypeCounts

                if (raw) {
                  const nextTypes = {}
                  for (const bucket in raw) {
                    nextTypes[bucket] = Object.entries(raw[bucket]).reduce((sum, [colKey, v]) => {
                      if (root._excludedCols?.[colKey] || root._hiddenCols?.[colKey]) return sum
                      return sum + (Number(v) || 0)
                    }, 0)
                  }
                  root.update({ totalByType: nextTypes })
                }
                el.call('refreshTicketsCounts')
                s.update({ hasChanges: false })
              }
            }
          },

          ExcludedColsList: {
            flow: 'y',
            gap: 'Y',
            childrenAs: 'state',
            children: (el, s) =>
              ticketsColKeys(s.root).map((key) => ({
                key,
                label: ticketsColLabel(el, s.root, key)
              })),
            childExtends: {
              tag: 'label',
              flow: 'x',
              align: 'center flex-start',
              gap: 'A',
              padding: 'Y Z1',
              round: 'radiusControl',
              background: 'line-faded',
              cursor: 'pointer',
              NativeCheckbox: {
                tag: 'input',
                attr: {
                  type: 'checkbox',
                  checked: (el, s) => !!(s.root?._excludedCols || {})[s.key]
                },
                margin: '0',
                cursor: 'pointer',
                onInput: (e, el, s) => {
                  const excluded = { ...(s.root?._excludedCols || {}) }
                  if (e.target.checked) excluded[s.key] = true
                  else delete excluded[s.key]
                  el.call('setTicketsBoardPref', { _excludedCols: excluded })
                  const block = el.lookup('ExcludedColsBlock')
                  block?.state?.update({ hasChanges: true })
                }
              },
              ColName: {
                tag: 'span',
                fontSize: 'Z1',
                color: 'title',
                userSelect: 'none',
                text: (el, s) => s.label
              }
            }
          }
        }
      }
    }
  }
}
